import { useTranslationWithPrefix } from '@hotjar/translations';
import { Text } from '@hotjar/hotsauce';
import { PriceReview } from '~/subscription-management/features/price-review/PriceReview';
import { usePrettifyPrice } from '~/subscription-management/utils/cost/prettify-price';
import type { Currency } from '../../../../api/common/model/customer';
import type { BillingPeriod } from '~/subscription-management/api/common/model/subscription';
import type { DiscountsType } from '~/subscription-management/components/discount/Discounts';

interface PriceDescriptionProps {
  taxAmount: number | undefined;
}

function PriceDescription({ taxAmount }: PriceDescriptionProps): JSX.Element | null {
  const { t: tReviewOrder } = useTranslationWithPrefix('checkout:wizard.review-order');
  if (taxAmount !== undefined && taxAmount !== 0) {
    return (
      <Text textType="caption" textColor="text_light">
        {tReviewOrder('price-description')}
      </Text>
    );
  }
  return null;
}

interface PriceReviewTodayChargeProps {
  currency: Currency;
  payNowAmountWithTax: number;
  loading: boolean;
  taxAmount?: number;
  className?: string;
}

export function PriceReviewTodayCharge({
  currency,
  payNowAmountWithTax,
  loading,
  taxAmount,
  className,
}: PriceReviewTodayChargeProps): JSX.Element {
  const { t: tReviewOrder } = useTranslationWithPrefix('checkout:wizard.review-order');
  const { prettifyPrice } = usePrettifyPrice(currency);
  return (
    <PriceReview loading={loading} className={className}>
      <PriceReview.Price
        loading={loading}
        price={prettifyPrice(payNowAmountWithTax)}
        title={tReviewOrder('current-charge.title')}
      />
      <PriceReview.ExtraInfo
        subtitle={tReviewOrder('current-charge.subtitle')}
        subtitleTooltip={tReviewOrder('current-charge.subtitle-tooltip')}
        priceDescription={<PriceDescription taxAmount={taxAmount} />}
      />
    </PriceReview>
  );
}

interface PriceReviewNextRecurringProps {
  currency: Currency;
  renewWithAmountWithTax: number;
  loading: boolean;
  taxAmount?: number;
  billingPeriod: BillingPeriod;
  accountDiscounts: DiscountsType[];
  nextChargeDate?: string;
  className?: string;
}

export function PriceReviewNextRecurring({
  currency,
  accountDiscounts,
  renewWithAmountWithTax,
  billingPeriod,
  taxAmount,
  loading,
  className,
  nextChargeDate,
}: PriceReviewNextRecurringProps): JSX.Element {
  const { t: tReviewOrder } = useTranslationWithPrefix('checkout:wizard.review-order');
  const { prettifyPrice } = usePrettifyPrice(currency);
  return (
    <PriceReview loading={loading} className={className}>
      <PriceReview.Price
        loading={loading}
        price={prettifyPrice(renewWithAmountWithTax)}
        title={tReviewOrder(`new-charge.title-${billingPeriod.toLowerCase()}`)}
      />
      <PriceReview.ExtraInfo
        subtitle={
          nextChargeDate !== undefined
            ? tReviewOrder(`new-charge.subtitle`, { date: nextChargeDate })
            : undefined
        }
        priceDescription={<PriceDescription taxAmount={taxAmount} />}
      />
      <PriceReview.AccountDiscount
        accountDiscounts={accountDiscounts}
        currency={currency}
        loading={loading}
      />
    </PriceReview>
  );
}
